// The focused ballot — how a category-challenged arrival sees today's play.
//
// A friend sent them "Beat me at Sports this week." Web is the FIRST surface
// they touch (they have no app yet), so the first screen has to be recognisably
// about Sports, not a generic six with Sports buried at position four. This
// module decides that order and nothing else — it is deliberately pure so the
// rule is readable, testable, and can't drift into a render function.
//
// The order (Tom 2026-07-28), and it is SIX CARDS, not nine:
//   1. today's SIX question in the focus category — "if available", because a
//      given day's six may simply not include one
//   2. that category's three BACK SECTION questions
//   3. TWO other daily-six questions
//
// Six cards is the point. Predict Six is a six-question ritual, and a
// nine-card wall is a different, worse first screen — so the arena is woven
// INTO the familiar shape rather than bolted on top of it. 1+3 is exactly the
// density the FP-6 scorer counts for a focused week, so four of those six
// cards are the game they were actually challenged to.
//
// THE REMAINING THREE ARE NOT DROPPED — they are DEFERRED, and the caller must
// present them. This is not stylistic: `awardSubmissionPoints` fires only when
// all six ids in dailySets/{today}.questionIds have votes, so a player who
// stopped here would silently earn no coins, start no streak, and never stamp
// lastSubmissionPointsDate — while Play's optimistic celebration had already
// promised them +6 and a streak. Hence `deferred`.
//
// Unfocused users get their six in the published order and nothing else; back
// sections reach them after they submit, via the interest picker.

/** The focused first ballot is always this many cards. 1 (focus) + 3 (section)
 *  + 2 (other) is the normal make-up; the "other" count is DERIVED from this
 *  target rather than hard-coded, so the shape survives the two days that
 *  don't fit the template — a six with no question in the arena at all, or a
 *  category whose section published fewer than three. In both cases more
 *  daily-six questions fill in and the user still sees six cards. */
export const FOCUSED_BALLOT_SIZE = 6;

/**
 * @param {Array<object>} six       today's six, in published order
 * @param {Array<object>} sections  the focus category's section questions ([] if none)
 * @param {string|null}   focusCategory  the arena, or null for a Classic arrival
 * @returns {{entries: Array<Entry>, deferred: Array<Entry>}} where
 *          Entry = {q, kind: 'six'|'section', index}. `index` is the position
 *          within that kind's own array — the caller routes an answer back to
 *          the right piece of state with it, which is why the kinds are tagged
 *          rather than merged. `deferred` is the daily-six questions held back
 *          from the first ballot; it is EMPTY for unfocused players.
 */
export function buildFocusedBallot(six, sections, focusCategory) {
  const sixList = Array.isArray(six) ? six : [];
  const sectionList = Array.isArray(sections) ? sections : [];

  const asSix = (q, index) => ({ q, kind: 'six', index });

  // No arena → the plain six, nothing deferred. Sections are not interleaved
  // for anyone who didn't arrive focused; they get them post-submit instead.
  if (!focusCategory) return { entries: sixList.map(asSix), deferred: [] };

  const leadIndex = sixList.findIndex((q) => q && q.category === focusCategory);

  const entries = [];
  // 1. The six's question in their arena, when today's set has one.
  if (leadIndex >= 0) entries.push(asSix(sixList[leadIndex], leadIndex));
  // 2. The back section — the questions that carry the week's scoring.
  sectionList.forEach((q, i) => entries.push({ q, kind: 'section', index: i }));
  // 3. Fill to six cards with other daily-six questions, published order kept.
  //    Normally that's exactly two. It self-corrects on the off days: no arena
  //    question in today's six → three others; a short section → more still.
  const otherCount = Math.max(0, FOCUSED_BALLOT_SIZE - entries.length);
  const others = sixList.map(asSix).filter((e) => e.index !== leadIndex);
  entries.push(...others.slice(0, otherCount));

  return { entries, deferred: others.slice(otherCount) };
}

/**
 * Is a question still answerable? A question that RESOLVED mid-day renders no
 * Yes/No buttons, so it can never be "answered" — treating it as outstanding
 * would permanently block Submit. Mirrors Play.jsx's `isResolvedCard` (audit
 * B2). A VOIDED card (correctAnswer === null) still renders buttons, so it
 * stays answerable.
 */
export function isAnswerable(q) {
  return !(q.isResolved && q.correctAnswer !== null);
}

/**
 * Submit-gate arithmetic for a ballot.
 *
 * Tom's call (2026-07-28): the focus category's section questions are REQUIRED
 * for a focused arrival — they're the scoring surface of the week their friend
 * challenged them to, so arriving, skipping them, and having a thin game is the
 * bad outcome. For everyone else sections are optional and reached later, so
 * they never raise the activation bar for an ordinary web visitor.
 *
 * @returns {{answered: number, total: number, complete: boolean}}
 */
export function ballotProgress(entries, { requireSections }) {
  const counted = entries.filter(
    (e) => (e.kind === 'six' || requireSections) && isAnswerable(e.q)
  );
  const answered = counted.filter((e) => typeof e.q.answer === 'boolean').length;
  return {
    answered,
    total: counted.length,
    complete: counted.length > 0 && answered === counted.length,
  };
}
